import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content, { Theme as ContentTheme } from 'components/base/Content'
import Img from 'components/base/Img'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import Title from 'components/base/Title'
import SectionRule from 'components/base/SectionRule/styles'
import List, { Theme as ListTheme } from 'components/base/List'
import Link from 'components/base/Link'
import Faq from 'components/blocks/Faq'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import AskQuestion from 'components/blocks/AskQuestion'
import InsuranceTypeIcon, { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'
import RetirementType from 'assets/images/products/RetirementType.svg'
import RetirementPlanning from 'assets/images/products/RetirementPlanning.svg'
import BeachChairs from 'assets/images/products/BeachChairs.svg'

const ProductsInvestmentsRetirement: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Investment & Retirement Insurance | Insurance Lounge'
      description='Learn how Insurance Lounge can help you set yourself up financially, so you can enjoy all the perks of retired life without worry.'
      ogImg='/images/og-images/Retirement.webp'
    />
    <PageHero
      cta={true}
      ctaTitle='Get an investment & retirement insurance quote now.'
      theme={PageHeroTheme.retirement}
      trackingInfo={{ page_category: 'product_page_investments_retirement' }}
    >
      Investments & Retirement in Oregon
    </PageHero>
    <Content short={true} margined={true} center={true}>
      <InsuranceTypeIcon theme={InsuranceTypeIconTheme.retirement} colored={true} hasMargin={true} />
      <Paragraph theme={ParagraphTheme.introLarge}>
        Everyone dreams of a life post-work. Days full of nothing but spending time with loved ones, traveling, and enjoying hobbies.
        Unfortunately, those luxuries often have to be earned. In order to enjoy retirement, you have to plan and make sound investments.
        Read on to learn how Insurance Lounge could help you set yourself up financially, so you can enjoy all the perks of retired life.
      </Paragraph>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={RetirementPlanning}
          src2x={undefined}
          style={{
            height: 180,
            width: 'auto',
          }}
          alt='Retirement Planning'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>Why Is Retirement Planning Important?</Title>
        <Paragraph>
          When you first begin working, or even after you’ve been in the workforce for 20 years,
          retirement can seem more like a faraway fantasy than a reality. Because retirement can seem so distant,
          many people fail to plan far enough ahead to ensure they can retire comfortably. The reality is that proper
          planning takes years of dedication, making it vital to start sooner rather than later. Retirement planning
          can help you avoid relying on government-sponsored retirement and allow you to pay for unforeseen medical expenses.
          The benefits from government-provided Social Security are meant to provide a basic safety net once you reach a certain age.
          But that minimum standard is often not enough for most individuals to have a fruitful and enjoyable retirement.
          Reasons why you shouldn’t rely on government-sponsored retirement:
        </Paragraph>
        <List theme={ListTheme.contentList}>
          <li>
            People live a lot longer now than they were when Social Security was first introduced in 1935.
            This means the pool of retires will continue to grow while those working and contributing their federal
            tax dollars will shrink. This will force the government to finance retirement benefits more adequately,
            causing the value of these benefits to decrease as time goes on or be canceled altogether.
            According to the Social Security Board of Trustees, the number of workers per
            retirement beneficiary will fall to 2.2 by 2035.
          </li>
          <li>
            It’s only natural that as people age, the chances of having severe medical issues increase.
            The cost of healthcare in the United States is already very high and unlikely to come down.
            To retire comfortably, you should have extra money saved for these medical expenses.
          </li>
          <li>
            In addition to potential medical issues, you might need to deal with other unforeseen expenses,
            such as a family member needing help in a state of crisis.
          </li>
          <li>
            If you have children, it’s only natural to want to leave them something behind after you pass on.
            Planning for your retirement in advance will allow you to leave your loved ones a nest egg they can
            use to improve their quality of life. Additionally, failing to plan for retirement could cause
            you to become a financial burden to your family.
          </li>
        </List>
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={BeachChairs}
          src2x={undefined}
          style={{
            height: 160,
            width: 'auto',
          }}
          alt='Beach Chairs'
        />
      </Content>
      <Title level={2}>When to Start Planning for Retirement</Title>
      <Paragraph>
        If possible, you should start planning for retirement as soon as you start working. Remember,
        slow and steady is the name of the game when it comes to saving for retirement.
        With that being said, it’s understandable that planning for retirement in your early 20s is
        easier said than done. However, because of the nature of compound interest, there is a
        huge difference in the value of your retirement savings when starting in your 20s
        instead of your 30s. Starting early will also give your more time to save and acquire
        other assets that can help make your retired years as comfortable as possible.
        The Average Retirement Age Data from the U.S. Census Bureau indicates that the average
        retirement age in the United States is 63 and that the average length of retirement is 18 years.
        So theoretically, you should be planning to have around 20 years’ worth of income ready when
        it’s time to retire. Here are some tips to help ensure you can build adequate retirement
        savings that will last you for the long haul:
      </Paragraph>
      <List theme={ListTheme.contentList}>
        <li>
          If your job offers one, sign up for a 401(k). The process is as simple as you deciding
          how much you want to be taken out of your paycheck, and then your employer puts the money
          into a 401(k) on your behalf. Some companies also offer to match the amount you put in by
          a certain percentage. This is on an individual business basis, so it’s best to discuss your
          401(k) benefits with your employer before assuming anything.
        </li>
        <li>
          As soon as you can, sign up for a Roth IRA. A Roth IRA is a savings account funded by your
          after-tax dollars, allowing you to withdraw your benefits tax-free when it’s time to retire.
        </li>
        <li>
          Invest in the stock market. Doing so can yield you a higher return on investment than a savings account.
          While this may be a daunting prospect while you’re in your early 20s, seeking guidance
          from an experienced stockbroker or financial advisor can be a tremendous help.
        </li>
        <li>
          Build yourself an emergency fund. As was discussed earlier, life is proven to have surprises
          from time to time. Because of this, it’s a good idea to have a separate savings account
          dedicated to emergencies.
        </li>
      </List>
    </Content>
    <Content theme={ContentTheme.gray}>
      <Content theme={ContentTheme.paddedBelow} center={true} short={true}>
        <Img
          src={RetirementType}
          src2x={undefined}
          style={{
            height: 160,
            width: 'auto',
          }}
          alt='Retirement'
        />
      </Content>
      <Content center={true} short={true}>
        <Title level={2}>Investment Options for Future Retirement</Title>
        <Paragraph>
          When thinking about retirement, it’s crucial to think about how you want to spend your years after leaving the workforce.
          Things like where you will live and how you will spend your free time are important to consider
          because depending on how you invest and save, some homes and activities may be unattainable if you
          don’t plan accordingly. It would be best to research which investment options will work best for
          your needs and yield the best return. As you likely already know, there is a certain amount of risk that
          comes with investing. One way to mitigate some of this risk is by having a diverse portfolio of investments.
          Our advisors can counsel you on the following areas:
        </Paragraph>
        <List theme={ListTheme.twoColumn}>
          <li>Asset Allocation</li>
          <li>Wealth Management</li>
          <li>Portfolio Optimization</li>
          <li>Financial Planning</li>
          <li>Risk Management</li>
          <li>Retirement Planning</li>
          <li>Education Planning</li>
          <li>Charitable Legacy Planning</li>
          <li>Business Succession Planning</li>
          <li>Estate Conversion</li>
        </List>
        <Paragraph>
          If you start investing young, you have time on your side, giving you time to recover from investment losses.
          While if you wait to start investing when you get older, it might be a better idea to opt for lower-risk investments.
          No matter your age, it’s never a bad time to start investing if you have the ability. With all the
          investment options available, you can likely find something that will work for you.
        </Paragraph>
      </Content>
    </Content>
    <Content theme={ContentTheme.yellow}>
      <Content short={true} center={true}>
        <Title level={2}>Frequently Asked Questions</Title>
        <Faq
          items={[
            {
              question: 'What is diversification?',
              answer: (
                <>
                  <Paragraph>
                    Investment professionals use diversification to maximize returns while reducing risk.
                    You can do the same by allocating your assets across various classes, including property,
                    cash, equities, bonds, and securities. In addition to spreading your investments over several
                    asset classes, you should spread your stock investments across different industry sectors.
                  </Paragraph>
                  <Paragraph>
                    Additionally, you should limit the number of high-risk companies in your portfolio.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'What are IRAs?',
              answer: (
                <>
                  <Paragraph>
                    IRAs (individual retirement account) are retirement accounts available at financial institutions
                    that allow you to save money for retirement in a tax-advantaged way. There are several types of IRAs,
                    including traditional IRAs, Roth IRAs, and SEP IRAs, to name a few. Traditional IRA contributions
                    are tax-deductible, while withdrawals are taxed. Roth IRAs are funded with after-tax dollars,
                    but withdrawals are tax-free. A SEP IRA is set up by an employer and permits the employer to
                    make contributions into the accounts of eligible employees.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'Should I invest in the stock market?',
              answer: (
                <>
                  <Paragraph>
                    Investing in the stock market can be highly risky if you don’t know what you are doing.
                    That’s why you would be well-advised to engage a financial adviser to help with stock market
                    investments. Keep in mind when investing for retirement, it is best to spread your investment
                    across five or six stocks that will generate good returns while minimizing risk.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'How much do you need to save for retirement?',
              answer: (
                <>
                  <Paragraph>
                    It can be overwhelming to to to plan for the unknowns. Some things you likely will
                    need to factor into your calculations are:
                  </Paragraph>
                  <List theme={ListTheme.contentList}>
                    <li>
                      Cost for housing (whether renting or owning)
                    </li>
                    <li>
                      Day-to-day living expenses
                    </li>
                    <li>
                      Travel plans
                    </li>
                    <li>
                      Life insurance
                    </li>
                    <li>
                      Healthcare
                    </li>
                  </List>
                  <Paragraph>
                    Over the years, though, the magic number needed for retirement has doubled as the cost of
                    living and demographics have changed. The best way to get started is to meet with a financial
                    advisor so they can help you talk through your individual retirement goals and help you plan accordingly.
                  </Paragraph>
                </>
              ),
            },
            {
              question: '401(k) vs. Pensions - What’s the difference?',
              answer: (
                <>
                  <Paragraph>
                    401(k) is a type of retirement savings plan that your employer offers.
                    Certain rules regulate a 401(k) plan, including that money you contribute is tax-deferred until you
                    take it out in retirement. Whereas an individual has control of their 401(k), pension funds
                    are generally managed prudently by fund managers who are tasked with ensuring that retirees
                    get the benefits they were promised. Employees do not have control of investment decisions
                    with a pension plan. With a pension plan, employers take on the risk of guaranteeing a retirement benefit.
                    Although they used to be common practice, they are now rare because companies usually opt for
                    providing 401k benefits as it lowers their business risk.
                  </Paragraph>
                </>
              ),
            },
            {
              question: 'How does Social Security work for retirement?',
              answer: (
                <>
                  <Paragraph>
                    Social Security is designed to provide you, your spouse, and other eligible family members with
                    benefits in the following instances: when you retire, if you become disabled, or when you die.
                    The minimum age for claiming retirement benefits is 62. Starting in the month when you reach full
                    retirement age (FRA), you can receive full Social Security benefits without limits on your earnings.
                    If you claim before reaching FRA, your benefits will be reduced. They are not lost, however,
                    and they increase while being withheld until you reach FRA.
                  </Paragraph>
                </>
              ),
            },
          ]}
        />
      </Content>
    </Content>
    <Content margined={true} center={true} short={true}>
      <Title level={2}>Partner With a Knowledgeable Financial Advisor</Title>
      <Paragraph>
        Unless you work in finance, it’s unlikely you are up to date with all the new best investment practices and trends.
        The experienced professionals at Insurance Lounge can help you reach your investment and retirement goals.
        From 401(k) planning to saving for your children’s college funds, our licensed experts can help you prepare
        for the future. Fill out our <Link to='/quote/new'>quote and buy form</Link> or stop by one of
        our <Link to='/locations/storefronts'>four storefronts in Oregon</Link> to be provided with recommendations
        based on your situation and speak with a live Insurance Concierge today.
      </Paragraph>
    </Content>
    <SectionRule />
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ProductsInvestmentsRetirement
